import { Record, List, Map } from 'immutable';
import { LEVEL_FLOW_TYPE } from '@/domain/approvals/constants';
import {
  ApproverResponse,
  ApproversResponse,
  ApproverRecord,
  ApproversList,
  ApproverData,
  AllApproverData,
  AllApproversResponse,
  AllApproversList,
} from '@/domain/approvals/types';

// factories
export const ApprovalItemFactory = Record({
  userGuid: '',
  email: '',
  status: 'draft',
  picture: '', // the default logo should be here
  id: null,
});

export const TagFactory = Record({
  userGuid: '',
  tag: '',
  status: '',
  picture: '',
});

export const UserEnhancedApprovalFactory = Record({
  userGuid: '',
  tag: '',
  status: '',
  picture: '',
  fullName: '',
  id: null,
});

export const ApprovalsGroupFactory = Record({
  group_title: '',
  group_id: '',
  created_at: '',
  approvers: List(),
  error: '',
  owner: null,
});

export const ApproverFactory = Record<ApproverData>({
  id: '',
  created_at: '',
  username: '',
  picture: null,
  role: '',
  fullName: '',
  phone: '',
});

export const AllApprovalUsersFactory = Record<AllApproverData>({
  id: '',
  username: '',
  picture: null,
  role: '',
  fullName: '',
  phone: '',
  isApprover: false,
});

export const AdvancedApprovalsGroupLevelFactory = Record({
  approvers: List(),
  minApprovers: '',
  minAmount: '',
  flowType: LEVEL_FLOW_TYPE.SEQUENTIAL,
  status: '',
});
export const AdvancedApprovalsGroupFactory = Record({
  id: '',
  name: '',
  owner: '',
  levels: List(),
  created_at: '',
});
export const PrevStateFactory = Record({ list: List() });

export const AutocompleteApprovalsFactory = Record({
  approver_ids: List(),
  basic_approval_group_id: null,
  advanced_approval_group_id: null,
});

export const VendorBasedApprovalsDefaultsFactory = Record({
  approvals: AutocompleteApprovalsFactory(),
  approval_required: false,
  start_approval_flow: false,
  add_creator_to_editors: false,
  add_creator_to_notifiers: false,
  additional_editors: List(),
});

export const vendorBasedApprovalsDefaultsAdapter = (data) =>
  VendorBasedApprovalsDefaultsFactory({
    ...data,
    additional_editors: List(data.additional_editors),
    approvals: AutocompleteApprovalsFactory(data.approvals),
  });

export const ApprovalsFactory = Record({
  list: List(),
  levelsList: List(),
  prevState: PrevStateFactory(),
  author: '',
  editable: false,
  dateCreated: '',
  currentSigners: [],
  signature: '',
  status: 'draft',
  rejectReason: null,
  rejectMessage: null,
  rejectReasonsList: Map(),
  approveFormFieldsList: List(),
  approvalGroups: List(),
  advancedApprovalGroups: List(),
  approvers: List(),
  approvalGroupsLoaded: false,
  advancedApprovalGroupsLoaded: false,
  approversLoaded: false,
  allApprovalUsers: List(),
  allApprovalUsersLoaded: false,
  rearrangingInProcess: 0,
  groupId: null,
  advancedGroupId: null,
  vendorBasedApprovalsDefaults: VendorBasedApprovalsDefaultsFactory(),
  vendorBasedApprovalsDefaultsLoading: false,
});

export function ApprovalsListAdapter(items = []) {
  return List(
    items.map(({ username, status, picture, userid, id }) =>
      ApprovalItemFactory({
        userGuid: userid,
        email: username,
        status,
        picture,
        id,
      }),
    ),
  );
}

export const AllApprovalUsersListAdapter = (items: AllApproversResponse = []): AllApproversList =>
  List(
    items.map(({ username, picture, id, is_approver, role, display_name, phone }) =>
      AllApprovalUsersFactory({
        fullName: display_name,
        isApprover: is_approver,
        phone,
        username,
        picture,
        id,
        role,
      }),
    ),
  );

export const ApprovalsGroupAdapter = (group) => ApprovalsGroupFactory({ ...group, approvers: List(group.approvers) });

export const ApprovalGroupsAdapter = (groupsData = []) => List(groupsData.map(ApprovalsGroupAdapter));

export const ApproverAdapter = (approver: ApproverResponse): ApproverRecord =>
  ApproverFactory({ ...approver, fullName: approver.display_name });

export const ApproversAdapter = (approversData: ApproversResponse = []): ApproversList =>
  List(approversData.map(ApproverAdapter));

export const AdvancedApprovalsGroupLevelAdapter = ({
  min_amount: minAmount,
  min_approvers: minApprovers,
  flow_type: flowType,
  approvers,
  nodes,
  ...rest
}) => AdvancedApprovalsGroupLevelFactory({ ...rest, minAmount, minApprovers, flowType, approvers: approvers || nodes });
export const AdvancedApprovalsGroupLevelsAdapter = (levels = []) =>
  List(levels.map(AdvancedApprovalsGroupLevelAdapter));
export const AdvancedApprovalGroupAdapter = (group) =>
  AdvancedApprovalsGroupFactory({ ...group, levels: AdvancedApprovalsGroupLevelsAdapter(group.levels) });
export const AdvancedApprovalsGroupAdapter = (groupsData = []) => List(groupsData.map(AdvancedApprovalGroupAdapter));

export function ApprovalRejectReasonListAdapter(items) {
  return Map(items);
}

export const approvalFieldOptionFactory = Record({
  value: '',
  label: '',
});

export const approvalFieldFactory = Record({
  group: '',
  label: '',
  name: '',
  type: '',
  value: '',
  mandatory: false,
  readonly: false,
  options: List(),
  display_value: '',
});

export const approvalFieldOptionAdapter = (value, label) => approvalFieldOptionFactory({ value, label });

export const approvalFieldsAdapter = (items = []) =>
  List(
    items.map((item) =>
      approvalFieldFactory({
        ...item,
        options: List(Object.entries(item.options || {}).map((option) => approvalFieldOptionAdapter(...option))),
      }),
    ),
  );

export function ApprovalsStoreAdapter({
  nodes,
  updated,
  next,
  creator,
  rejectReasons,
  group_id,
  levels,
  advanced_group_id,
  ...rest
} = {}) {
  return ApprovalsFactory({
    ...rest,
    dateCreated: updated,
    currentSigners: Array.isArray(next) ? next : [next],
    author: creator,
    list: ApprovalsListAdapter(nodes || []),
    levelsList: AdvancedApprovalsGroupLevelsAdapter(levels || []),
    rejectReasonsList: ApprovalRejectReasonListAdapter(rejectReasons || {}),
    approveFormFieldsList: approvalFieldsAdapter([]),
    groupId: group_id,
    advancedGroupId: advanced_group_id,
  });
}

type ApprovalNode = { status?: string | null; approvers?: ApprovalNode[]; nodes?: ApprovalNode[] };

const checkNode = (node?: ApprovalNode | null) => ['approved', 'rejected'].includes(node?.status ?? '');

/**
 * RETURN INVISIBILITY STATUS (hidden, isHidden) for edit document action
 * @param {string} status - of approval state
 * @param {array} nodes - array of approvals
 * @param {array} levels - array of approval levels
 * @returns {boolean}
 */
export const checkEditActionInvisibility = (
  status?: string | null,
  nodes: ApprovalNode[] = [],
  levels: ApprovalNode[] = [],
) => {
  if (status === 'active') return true;

  if (Array.isArray(nodes) && nodes.length > 0) return nodes.some(checkNode);

  if (Array.isArray(levels) && levels.length > 0) {
    return levels.some((l) => {
      if (checkNode(l)) return true;

      if (l?.approvers?.some(checkNode)) return true;

      return l?.nodes?.some(checkNode);
    });
  }

  return false;
};
